import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, unref as _unref, toDisplayString as _toDisplayString, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock, renderList as _renderList, Fragment as _Fragment, normalizeStyle as _normalizeStyle, mergeProps as _mergeProps, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = { class: "p-3 flex flex-col gap-5" }
const _hoisted_2 = { class: "flex justify-between items-center" }
const _hoisted_3 = { class: "flex gap-5 items-center" }
const _hoisted_4 = { class: "flex gap-5 text-sm text-gray-600" }
const _hoisted_5 = {
  key: 0,
  class: "fixed inset-0 bg-black/70 flex items-center justify-center z-50"
}
const _hoisted_6 = { class: "text-4xl font-bold mb-4" }
const _hoisted_7 = { class: "text-xl mb-6" }
const _hoisted_8 = ["onContextmenu"]
const _hoisted_9 = ["draggable", "onClick", "title"]
const _hoisted_10 = { class: "text-[32px] leading-none" }
const _hoisted_11 = {
  key: 0,
  class: "absolute top-0.5 right-0.5 bg-black/70 text-white text-[10px] px-1 py-0.5 rounded font-bold"
}
const _hoisted_12 = {
  key: 1,
  class: "absolute bottom-0 left-0 right-0 h-1 bg-black/10 rounded-b-sm overflow-hidden"
}
const _hoisted_13 = {
  key: 0,
  class: "tile-item ghost w-full h-full flex items-center justify-center"
}
const _hoisted_14 = { class: "text-[32px] leading-none" }
const _hoisted_15 = {
  key: 1,
  class: "w-full h-full opacity-30"
}
const _hoisted_16 = ["data-highlight"]

import { computed, watch, onMounted, onUnmounted, ref } from 'vue'
import { game } from '../game/init'
import { Tile } from '../game'
import { useDrag } from '../lib/useDrag'
import { useHover } from '../lib/useHover'
import { eq } from '../lib/algo'
import type { CompleteItemState } from '../data/types'

// Hover handling
const SAVE_KEY = 'shade-survival-save'


export default /*@__PURE__*/_defineComponent({
  __name: 'Grid',
  setup(__props) {

const hover = useHover<Tile>()

watch(hover.hovered, (tile, oldTile) => {
    // Don't show hover previews while dragging
    if (game.state.dragging) return

    if (tile) {
        if (tile.item) {
            game.emit('hover:item', { tile, item: tile.item })
        } else {
            game.emit('hover:update', { tile })
        }
    } else if (oldTile) {
        game.emit('hover:update', { tile: 'out-of-grid' })
    }
})

// Grid ref for calculating tile positions
const gridRef = ref<HTMLElement | null>(null)

// Get animation style with CSS variables for arc motion
function getAnimationStyle(item: CompleteItemState) {
    if (!item.animation || !gridRef.value) return {}

    const { from, to, type, endTick } = item.animation
    const duration = game.data.animations[type].duration

    // Use item's current position, fallback to animation from position
    const pos = item.position || from

    return {
        '--pos-x': pos.x.toString(),
        '--pos-y': pos.y.toString(),
        '--from-x': from.x.toString(),
        '--from-y': from.y.toString(),
        '--to-x': to.x.toString(),
        '--to-y': to.y.toString(),
        '--duration': duration.toString(),
    }
}

// Game loop
let loopId: number | undefined
let running = false

onMounted(() => {
    running = true

    const loop = () => {
        // Increment tick and emit to game
        const tick = game.state.tick + 1
        game.emit('tick', { tick })

        // Update hover preview (handles both drag and activation previews)
        game.emit('hover:update', { tile: 'not-changed' })

        if (running) {
            loopId = requestAnimationFrame(loop)
        }
    }

    loopId = requestAnimationFrame(loop)
})

onUnmounted(() => {
    running = false
    if (loopId !== undefined) {
        cancelAnimationFrame(loopId)
    }
})

// Save/Load functionality
function saveGame() {
    try {
        const saveData = game.saveState()
        localStorage.setItem(SAVE_KEY, JSON.stringify(saveData))
        console.log('Game saved!', JSON.parse(JSON.stringify(saveData)))
        alert('Game saved!')
    } catch (error) {
        console.error('Failed to save:', error)
        alert('Failed to save game')
    }
}

function getDiff(obj1: any, obj2: any, path = ''): any {
    if (obj1 === obj2) return undefined
    
    if (obj1 === null || obj2 === null || typeof obj1 !== 'object' || typeof obj2 !== 'object') {
        return { [path || 'root']: { original: obj1, reSerialized: obj2 } }
    }
    
    if (Array.isArray(obj1) !== Array.isArray(obj2)) {
        return { [path || 'root']: { original: obj1, reSerialized: obj2 } }
    }
    
    const diff: any = {}
    const allKeys = new Set([...Object.keys(obj1), ...Object.keys(obj2)])
    
    for (const key of allKeys) {
        const newPath = path ? `${path}.${key}` : key
        const val1 = obj1[key]
        const val2 = obj2[key]
        
        if (!(key in obj1)) {
            diff[key] = { original: undefined, reSerialized: val2 }
        } else if (!(key in obj2)) {
            diff[key] = { original: val1, reSerialized: undefined }
        } else {
            const subDiff = getDiff(val1, val2, newPath)
            if (subDiff !== undefined) {
                Object.assign(diff, subDiff)
            }
        }
    }
    
    return Object.keys(diff).length > 0 ? diff : undefined
}

function loadGame() {
    try {
        const saved = localStorage.getItem(SAVE_KEY)
        if (!saved) {
            alert('No save found')
            return
        }
        const saveData = JSON.parse(saved)
        game.loadState(saveData)
        
        // Verify serialization round-trip
        const reSerialized = game.saveState()
        const diff = getDiff(saveData, reSerialized)
        
        if (diff !== undefined) {
            console.error('Serialization mismatch!', diff)
            alert('WARNING: Serialized game state does not match after loading! Check console for details.')
        }
        
        console.log('Game loaded!', saveData)
        alert('Game loaded!')
    } catch (error) {
        console.error('Failed to load:', error)
        alert('Failed to load game')
    }
}

// Keyboard shortcuts
onMounted(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
        if (e.ctrlKey || e.metaKey) {
            if (e.key === 's') {
                e.preventDefault()
                saveGame()
            } else if (e.key === 'l') {
                e.preventDefault()
                loadGame()
            }
        }
    }
    window.addEventListener('keydown', handleKeyPress)

    return () => {
        window.removeEventListener('keydown', handleKeyPress)
    }
})

// Drag & drop handling - emit events, no logic here
const drag = useDrag<Tile>((from, to) => {
    game.emit('manual:drop', { from, to, item: from.item! })
})

// Bridge useDrag refs to game events
watch(() => drag.dragging.value, (from) => {
    if (from) {
        hover.clear()  // Clear hover when drag starts
        game.emit('drag:start', { from })
    } else {
        game.emit('drag:end', {})
        // Update hover preview after drag ends
        game.emit('hover:update', { tile: 'not-changed' })
    }
})

watch(() => drag.over.value, (over) => {
    game.emit('drag:over', { over: over ?? null })
})

const tiles = computed(() => Array.from(game.state.tiles.values()).sort((a, b) => {
    if (a.position.y !== b.position.y) return a.position.y - b.position.y
    return a.position.x - b.position.x
}))

// Register tiles for touch hit testing (drag and hover)
watch(tiles, (newTiles, oldTiles) => {
    // Unregister old tiles
    if (oldTiles) {
        for (const tile of oldTiles) {
            drag.unregisterTarget(tile.key)
            hover.unregister(tile.key)
        }
    }
    // Register new tiles
    for (const tile of newTiles) {
        drag.registerTarget(tile.key, tile)
        hover.register(tile.key, tile)
    }
}, { immediate: true })

const itemCount = computed(() => {
    return Array.from(game.state.tiles.values()).filter(t => t.item).length
})

function restartGame() {
    // Reload the page to restart
    window.location.reload()
}

const gridStyle = computed(() => {
    const width = Math.max(...tiles.value.map(t => t.position.x)) + 1
    const height = Math.max(...tiles.value.map(t => t.position.y)) + 1
    return {
        gridTemplateColumns: `repeat(${width}, 1fr)`,
        gridTemplateRows: `repeat(${height}, 1fr)`,
        userSelect: 'none' as const
    }
})

function getChargePercent(item: CompleteItemState): number {
    if (!item.data.charge || item.charge === undefined) return 0
    return Math.min(100, (item.charge / item.data.charge.max) * 100)
}

function onItemClick(tile: Tile) {
    if (tile.item) {
        game.emit('manual:activate', { tile })
        // Update highlight after activation (will recalculate based on new item state)
        game.emit('hover:update', { tile: 'not-changed' })
    }
}

function handleRightClick(tile: Tile) {
    if (tile.item) {
        game.emit('manual:activate', { tile })
        // Update highlight after activation (will recalculate based on new item state)
        game.emit('hover:update', { tile: 'not-changed' })
    }
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _cache[0] || (_cache[0] = _createElementVNode("h2", { class: "m-0 text-2xl" }, "Shade Survival", -1 /* CACHED */)),
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("div", { class: "flex gap-2" }, [
          _createElementVNode("button", {
            onClick: saveGame,
            class: "px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700",
            title: "Save (Ctrl+S)"
          }, " Save "),
          _createElementVNode("button", {
            onClick: loadGame,
            class: "px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700",
            title: "Load (Ctrl+L)"
          }, " Load ")
        ]),
        _createElementVNode("div", _hoisted_4, [
          _createElementVNode("span", null, "Tick: " + _toDisplayString(_unref(game).state.tick), 1 /* TEXT */),
          _createElementVNode("span", null, "Items: " + _toDisplayString(itemCount.value), 1 /* TEXT */)
        ])
      ])
    ]),
    _createCommentVNode(" Victory/Defeat overlay "),
    (_unref(game).state.gameOver !== 'playing')
      ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
          _createElementVNode("div", {
            class: _normalizeClass(["text-center p-10 rounded-lg", _unref(game).state.gameOver === 'victory' ? 'bg-green-900' : 'bg-red-900'])
          }, [
            _createElementVNode("h1", _hoisted_6, _toDisplayString(_unref(game).state.gameOver === 'victory' ? 'VICTORY!' : 'DEFEAT'), 1 /* TEXT */),
            _createElementVNode("p", _hoisted_7, _toDisplayString(_unref(game).state.gameOver === 'victory' ? 'All emitters destroyed!' : 'The shade consumed you...'), 1 /* TEXT */),
            _createElementVNode("button", {
              onClick: restartGame,
              class: "px-6 py-3 bg-white text-black rounded font-bold hover:bg-gray-200"
            }, " Play Again ")
          ], 2 /* CLASS */)
        ]))
      : _createCommentVNode("v-if", true),
    _createElementVNode("div", {
      ref_key: "gridRef",
      ref: gridRef,
      class: "grid bg-gray-300 p-0.5 rounded max-w-fit relative",
      style: _normalizeStyle({...gridStyle.value, '--tile-size': '52px'})
    }, [
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(tiles.value, (tile) => {
        return (_openBlock(), _createElementBlock("div", _mergeProps({
          key: tile.key,
          class: ["tile aspect-square min-w-[50px] min-h-[50px] bg-gray-100 border border-gray-300 rounded-sm relative transition-colors hover:bg-gray-200", {
                    'bg-white': tile.item && !tile.item.animation
                }],
          style: {
                    gridColumn: tile.position.x + 1,
                    gridRow: tile.position.y + 1
                }
        }, { ref_for: true }, { ..._unref(drag).target(tile, tile.key), ..._unref(hover).bind(tile, tile.key) }, {
          onContextmenu: _withModifiers(($event: any) => (handleRightClick(tile)), ["prevent"])
        }), [
          _createCommentVNode(" Item "),
          (tile.item)
            ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                _createCommentVNode(" The actual item - moves when dragging "),
                _createElementVNode("div", _mergeProps({
                  class: "tile-item w-full h-full flex items-center justify-center relative cursor-grab",
                  draggable: tile.shade === 0 && !tile.item.animation
                }, { ref_for: true }, tile.shade === 0 && !tile.item.animation ? _unref(drag).source(tile) : {}, {
                  onClick: ($event: any) => (onItemClick(tile)),
                  title: tile.item.id,
                  class: [
                            { 'opacity-50 cursor-not-allowed': tile.shade > 0 },
                            tile.item.animation ? tile.item.animation.type : ''
                        ],
                  style: tile.item.animation ? getAnimationStyle(tile.item) : {}
                }), [
                  _createElementVNode("span", _hoisted_10, _toDisplayString(tile.item.data.display?.emoji ?? '📦'), 1 /* TEXT */),
                  (tile.item.charge !== undefined && tile.shade === 0)
                    ? (_openBlock(), _createElementBlock("span", _hoisted_11, _toDisplayString(tile.item.charge), 1 /* TEXT */))
                    : _createCommentVNode("v-if", true),
                  (tile.item.data.charge && tile.shade === 0)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_12, [
                        _createElementVNode("div", {
                          class: "h-full bg-green-500",
                          style: _normalizeStyle({ width: getChargePercent(tile.item) + '%' })
                        }, null, 4 /* STYLE */)
                      ]))
                    : _createCommentVNode("v-if", true)
                ], 16 /* FULL_PROPS */, _hoisted_9),
                _createCommentVNode(" Ghost that stays in original position "),
                (_unref(game).state.dragging?.from && _unref(game).state.dragging.from.x === tile.position.x && _unref(game).state.dragging.from.y === tile.position.y)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_13, [
                      _createElementVNode("span", _hoisted_14, _toDisplayString(tile.item.data.display?.emoji ?? '📦'), 1 /* TEXT */)
                    ]))
                  : _createCommentVNode("v-if", true)
              ], 64 /* STABLE_FRAGMENT */))
            : (!tile.item)
              ? (_openBlock(), _createElementBlock("div", _hoisted_15))
              : _createCommentVNode("v-if", true),
          _createCommentVNode(" Shade overlay "),
          (tile.shade > 0)
            ? (_openBlock(), _createElementBlock("div", {
                key: 2,
                class: "shade-overlay pointer-events-none rounded-sm text-gray-400",
                style: _normalizeStyle({
                        backgroundColor: `rgba(20, 10, 30, ${Math.min(tile.shade / 50, 0.95)})`,
                    })
              }, _toDisplayString(tile.shade), 5 /* TEXT, STYLE */))
            : _createCommentVNode("v-if", true),
          _createCommentVNode(" Highlight overlay (appears on top of shade) "),
          (_unref(game).state.highlight?.[tile.key])
            ? (_openBlock(), _createElementBlock("div", {
                key: 3,
                class: "highlight-overlay pointer-events-none rounded-sm",
                "data-highlight": _unref(game).state.highlight?.[tile.key]
              }, null, 8 /* PROPS */, _hoisted_16))
            : _createCommentVNode("v-if", true)
        ], 16 /* FULL_PROPS */, _hoisted_8))
      }), 128 /* KEYED_FRAGMENT */))
    ], 4 /* STYLE */)
  ]))
}
}

})